import { Component } from '@theme/component';

/**
 * Featured collection tabs
 *
 * Switches collection panels with a fade transition and updates only the
 * Shop all button href when the active tab changes.
 *
 * @typedef {Object} FeaturedCollectionTabsRefs
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} [panels]
 * @property {HTMLAnchorElement} [shopAll]
 *
 * @extends {Component<FeaturedCollectionTabsRefs>}
 */
class FeaturedCollectionTabsComponent extends Component {
  requiredRefs = ['tabs'];

  /** @type {number} */
  #activeIndex = 0;

  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    this.#activeIndex = Math.max(
      0,
      tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );

    this.#abort?.abort();
    this.#abort = new AbortController();
    this.addEventListener('keydown', this.#handleKeydown, { signal: this.#abort.signal });

    this.#sync(this.#activeIndex, { animate: false });

    // Theme block panels may register refs slightly after connect.
    if (!this.refs.panels?.length) {
      queueMicrotask(() => this.#sync(this.#activeIndex, { animate: false }));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  /**
   * Select a tab by index.
   *
   * @param {number} index
   * @param {Event} [event]
   */
  select(index, event) {
    event?.preventDefault();

    const nextIndex = Number(index);
    if (Number.isNaN(nextIndex) || nextIndex === this.#activeIndex) return;
    if (!this.refs.tabs?.[nextIndex]) return;

    this.#sync(nextIndex, { animate: true });
  }

  /**
   * @param {number} index
   * @param {{ animate?: boolean }} [options]
   */
  #sync(index, options = {}) {
    const { animate = true } = options;
    const { tabs, panels, shopAll } = this.refs;

    if (!tabs?.length) return;

    this.#activeIndex = index;

    for (const [i, tab] of tabs.entries()) {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      tab.classList.toggle('is-active', selected);
    }

    if (panels?.length) {
      for (const [i, panel] of panels.entries()) {
        const selected = i === index;
        panel.classList.toggle('is-active', selected);
        panel.toggleAttribute('inert', !selected);
        panel.setAttribute('aria-hidden', String(!selected));

        if (!animate) {
          panel.classList.toggle('is-visible', selected);
        }
      }

      if (animate) {
        this.#animatePanels(index);
      }
    }

    this.#updateShopAll(index);
  }

  /**
   * @param {number} index
   */
  #updateShopAll(index) {
    const { tabs, panels, shopAll } = this.refs;
    if (!(shopAll instanceof HTMLAnchorElement)) return;

    const activePanel = panels?.[index];
    const activeTab = tabs?.[index];
    const shopAllUrl =
      activePanel?.getAttribute('data-shop-all-url') ||
      activeTab?.getAttribute('data-shop-all-url') ||
      '';

    if (shopAllUrl) {
      shopAll.href = shopAllUrl;
      shopAll.removeAttribute('aria-disabled');
      shopAll.removeAttribute('role');
      shopAll.classList.remove('is-disabled');
    } else {
      shopAll.removeAttribute('href');
      shopAll.setAttribute('role', 'link');
      shopAll.setAttribute('aria-disabled', 'true');
      shopAll.classList.add('is-disabled');
    }
  }

  /**
   * Crossfade panels while keeping layout height from the active panel.
   *
   * @param {number} index
   */
  #animatePanels(index) {
    const { panels } = this.refs;
    if (!panels?.length) return;

    for (const [i, panel] of panels.entries()) {
      const selected = i === index;

      if (selected) {
        panel.classList.add('is-visible');
      } else {
        panel.classList.remove('is-visible');
      }
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.getAttribute('role') !== 'tab') return;

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const currentIndex = tabs.indexOf(target);
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    tabs[nextIndex]?.focus();
    this.select(nextIndex);
  };
}

if (!customElements.get('featured-collection-tabs-component')) {
  customElements.define('featured-collection-tabs-component', FeaturedCollectionTabsComponent);
}
